import {
  useEffect,
  useState,
  FormEvent,
  ChangeEvent,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import utils from "../../helpers/utils";
import students from "../../services/students";
import appointment from "../../services/appointment";
import { toast } from "react-toastify";
import { Student, CreateAppointmentPayload, Appointment } from "../../types";
import { FiX, FiCalendar, FiPlus, FiSave } from "react-icons/fi";
import CustomCalendar from "../../components/ui/CustomCalendar";
import ServiceTab, { type ServiceTabHandle } from "../profile/ServiceTab";

interface NewAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  idStudent?: string | number | null;
  onSuccess?: () => void;
  initialData?: Appointment | null;
}

const initform = {
  date_time: new Date().toISOString(),
  duration_minutes: 0,
  type: "",
};

function NewAppointmentModal({
  isOpen,
  onClose,
  idStudent = null,
  onSuccess = () => {},
  initialData = null,
}: NewAppointmentModalProps) {
  const { getUser } = useAuth();
  const professional_id = getUser()?.id;

  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString(),
  );
  const [alunos, setAlunos] = useState<Student[]>([]);
  const [services, setServices] = useState<
    Array<{ id: number; name: string; duration_minutes: number }>
  >([]);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const servicesRef = useRef<ServiceTabHandle>(null);
  const [servicesFooter, setServicesFooter] = useState({
    hasChanges: false,
    saving: false,
    hasErrors: false,
  });
  const [form, setForm] = useState({
    ...initform,
    user_id: idStudent ?? "",
    professional_id: professional_id,
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Converter para timezone de São Paulo antes de exibir
        const saoPauloDate = utils.toSaoPauloISOString(
          new Date(initialData.start_time),
        );
        setForm({
          user_id: initialData.user.id,
          professional_id: professional_id,
          date_time: saoPauloDate,
          duration_minutes: initialData.duration_minutes,
          type: initialData.type,
        });
        setSelectedDate(saoPauloDate);
      } else {
        // Usar horário atual em São Paulo
        const now = utils.toSaoPauloISOString(new Date());
        setForm({
          ...initform,
          user_id: idStudent ?? "",
          professional_id: professional_id,
          date_time: now,
        });
        setSelectedDate(now);
      }
    }
  }, [isOpen, idStudent, professional_id, initialData]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: CreateAppointmentPayload = {
      user_id: Number(form.user_id!),
      professional_id: Number(form.professional_id!),
      date_time: form.date_time.slice(0, 16),
      duration_minutes: Number(form.duration_minutes),
      type: form.type,
    };

    if (initialData && initialData.id) {
      const res = await appointment.updateAppointment(
        String(initialData.id),
        String(professional_id),
        payload,
      );
      if (res) {
        toast.success("Compromisso atualizado com sucesso!");
        onSuccess();
      }
    } else {
      const res = await appointment.createAppointment(payload);
      if (res) {
        toast.success("Compromisso criado com sucesso!");
        onSuccess();
      }
    }
  };

  const loadStudents = useCallback(async () => {
    if (professional_id) {
      const data = await students.listAll(String(professional_id));
      if (data?.students) {
        setAlunos(data.students);
      } else {
        setAlunos([]);
      }
    }
  }, [professional_id]);

  const loadServices = useCallback(async () => {
    if (!professional_id) return;
    try {
      const data = await appointment.getProfessionalServices(
        String(professional_id),
      );
      setServices(Array.isArray(data) ? data : []);
    } catch {
      setServices([]);
    }
  }, [professional_id]);

  useEffect(() => {
    if (isOpen) {
      loadStudents();
      loadServices();
    }
  }, [isOpen, loadStudents, loadServices]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, user_id: idStudent ?? "" }));
  }, [idStudent]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      date_time: selectedDate,
    }));
  }, [selectedDate]);

  const handleChange = (target: { name: string; value: string | number }) => {
    setForm({ ...form, [target.name]: target.value });
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm overflow-y-auto overflow-x-hidden sm:p-4">
        <div className="relative flex h-[95vh] w-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark my-auto sm:max-w-3xl sm:rounded-xl md:rounded-2xl">
          {/* Header Premium - Similar à página de alunos */}
          <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 py-3 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30 sm:px-6 sm:py-5 md:px-8 md:py-6">
            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

            <button
              onClick={onClose}
              className="absolute right-2 top-2 z-10 rounded-lg p-1.5 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 sm:right-4 sm:top-4 sm:rounded-xl sm:p-2 md:right-6 md:top-6"
              aria-label="Fechar modal"
            >
              <FiX size={18} />
            </button>

            <div className="relative flex items-start gap-2 pr-8 sm:gap-4 sm:pr-0 md:gap-5">
              {/* Icon com Ring */}
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-75 blur-sm"></div>
                <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-purple-600 shadow-xs dark:border-boxdark sm:h-12 sm:w-12 sm:border-3 md:h-14 md:w-14 md:border-4">
                  <FiCalendar className="text-white" size={18} />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl">
                    {initialData ? "Editar Compromisso" : "Novo Compromisso"}
                  </h2>
                </div>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mt-1 sm:text-sm">
                  Agende um novo compromisso ou edite um existente
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 min-w-0 space-y-3 overflow-y-auto overflow-x-hidden bg-gray-50/50 p-3 dark:bg-gray-900/20 sm:space-y-4 sm:p-5 md:space-y-6 md:p-8">
              <div className="rounded-lg border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:rounded-2xl md:p-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-6">
                  <div>
                    <label className="mb-2 block text-xs font-bold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm md:mb-3">
                      Aluno <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="user_id"
                        name="user_id"
                        required
                        value={form.user_id as string | number}
                        onChange={({ target }) => handleChange(target)}
                        className="w-full appearance-none rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-900 outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 sm:rounded-xl sm:px-4 sm:py-3 md:px-5 md:py-3.5"
                      >
                        <option
                          value=""
                          disabled
                          className="text-body dark:text-bodydark"
                        >
                          Selecione um aluno
                        </option>
                        {alunos.map((e: Student) => (
                          <option
                            key={e.id}
                            value={e.user.id}
                            className="text-body dark:text-bodydark"
                          >
                            {e.user.name}
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 sm:right-4">
                        <svg
                          className="fill-current"
                          width="18"
                          height="18"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 sm:mb-2.5 md:mb-3">
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 sm:text-sm">
                        Tipo de Aula (serviço){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setServicesModalOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-purple-600 bg-white px-2.5 py-1.5 text-xs font-semibold text-purple-600 transition hover:bg-purple-50 dark:border-purple-500 dark:bg-boxdark dark:text-purple-400 dark:hover:bg-purple-900/30 sm:px-3 sm:py-2 sm:text-sm"
                      >
                        <FiPlus size={14} />
                        Novo serviço
                      </button>
                    </div>
                    <div className="relative">
                      <select
                        id="type"
                        name="type"
                        value={form.type}
                        onChange={({ target }) => {
                          const svc = services.find(
                            (s) => s.name === target.value,
                          );
                          setForm({
                            ...form,
                            type: target.value,
                            duration_minutes: svc?.duration_minutes ?? 0,
                          });
                        }}
                        required
                        className="w-full appearance-none rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-900 outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 sm:rounded-xl sm:px-4 sm:py-3 md:px-5 md:py-3.5"
                      >
                        <option
                          value=""
                          disabled
                          className="text-body dark:text-bodydark"
                        >
                          Selecione o serviço
                        </option>
                        {form.type &&
                          !services.some((s) => s.name === form.type) && (
                            <option
                              value={form.type}
                              className="text-body dark:text-bodydark"
                            >
                              {form.type} ({form.duration_minutes} min)
                            </option>
                          )}
                        {services.map((svc) => (
                          <option
                            key={svc.id}
                            value={svc.name}
                            className="text-body dark:text-bodydark"
                          >
                            {svc.name} ({svc.duration_minutes} min)
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 sm:right-4">
                        <svg
                          className="fill-current"
                          width="18"
                          height="18"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </div>
                    {form.type && form.duration_minutes > 0 && (
                      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        Duração: {form.duration_minutes} min
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-bold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm md:mb-3">
                      Data e Hora <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="date_time"
                      name="date_time"
                      required
                      value={selectedDate ? selectedDate.slice(0, 16) : ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const newDate = e.target.value;
                        // O input datetime-local retorna "YYYY-MM-DDTHH:MM" no timezone local
                        // Assumimos que o usuário está selecionando o horário pensando em São Paulo
                        // Então usamos o valor diretamente (a API espera no timezone de São Paulo)
                        setSelectedDate(newDate);
                        setForm({ ...form, date_time: newDate });
                      }}
                      className="w-full rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-900 outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 sm:rounded-xl sm:px-4 sm:py-3 md:px-5 md:py-3.5"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:rounded-2xl md:p-6">
                <label className="mb-2 block text-xs font-bold text-gray-700 dark:text-gray-300 sm:mb-3 sm:text-sm md:mb-4">
                  Selecione a Data no Calendário
                </label>
                <div className="w-full min-w-0 overflow-hidden rounded-lg border border-stroke bg-gray-50 p-2 dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-3 md:p-4">
                  <CustomCalendar
                    onChange={(date) => {
                      // Converter para timezone de São Paulo ao selecionar data
                      const saoPauloDate = utils.toSaoPauloISOString(date);
                      setSelectedDate(saoPauloDate);
                      setForm({ ...form, date_time: saoPauloDate });
                    }}
                    value={selectedDate}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-stroke bg-white px-3 py-3 dark:border-strokedark dark:bg-boxdark sm:px-5 sm:py-4 md:px-8 md:py-6">
              <div className="flex flex-col justify-end gap-2 sm:flex-row sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-lg border border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800/50 sm:w-auto sm:rounded-xl sm:px-5 sm:text-base md:px-6 md:py-3"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:scale-105 hover:shadow-xl sm:w-auto sm:rounded-xl sm:px-6 sm:text-base md:px-8 md:py-3"
                >
                  <FiCalendar size={16} />
                  {initialData
                    ? "Atualizar Compromisso"
                    : "Agendar Compromisso"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {servicesModalOpen && (
        <div
          className="fixed inset-0 z-[1000000] flex items-end justify-center bg-black/60 p-0 overflow-y-auto backdrop-blur-sm sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setServicesModalOpen(false);
              loadServices();
            }
          }}
        >
          <div
            className="relative flex h-screen w-full flex-col overflow-hidden rounded-t-2xl border-t border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:h-screen sm:max-w-3xl sm:rounded-2xl sm:border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-stroke px-3 py-3 dark:border-strokedark sm:px-6 sm:py-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">
                Serviços
              </h3>
              <button
                type="button"
                onClick={() => {
                  setServicesModalOpen(false);
                  loadServices();
                }}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label="Fechar"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6">
              <ServiceTab
                ref={servicesRef}
                hideIndicators
                hideFooter
                onStateChange={setServicesFooter}
              />
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-stroke bg-white px-3 py-3 dark:border-strokedark dark:bg-boxdark sm:flex-row sm:justify-end sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={() => {
                  setServicesModalOpen(false);
                  loadServices();
                }}
                className="rounded-lg border border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await servicesRef.current?.save();
                  if (ok) {
                    loadServices();
                    setServicesModalOpen(false);
                  }
                }}
                disabled={
                  !servicesFooter.hasChanges ||
                  servicesFooter.saving ||
                  servicesFooter.hasErrors
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-3"
              >
                {servicesFooter.saving ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiSave size={18} />
                    Salvar alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

export default NewAppointmentModal;
